import React, { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from "react-router-dom";
import { setTabSelected } from '../redux/data/dataSlice';
import { getRewardSections } from '../redux/data/dataActions';
import { setupMetaData, setupBaseMetaData } from "../utils/metaData";
import { injectPageScripts } from "../utils/loadScripts";
import config from "../config/memberhubContents.json";
import RewardSection from "./rewardSection";
import RewardModal from "./rewardModal";
import TabSectionHeader from "./tabSectionHeader";

import "../assets/css/header.css";
import "../assets/css/modal.css";

function Benefits() {

    const dispatch = useDispatch();
    const params = useParams();
    const refBenefits = useRef();

    const [rewardCount, setRewardCount] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [refreshBenefits, setRefreshBenefits] = useState(true);
    const [selectedReward, setSelectedReward] = useState(null);

    const userType = useSelector(state => state.data.userType);
    const rewardSections = useSelector(state => state.data.rewardSections);

    let openModal = (reward) => {
        setShowModal(true);
        setSelectedReward(reward);
    };

    useEffect(() => {
        // analytics variable
        const clickedOnBenefit = window.location.search.includes("cl=1"); // as opposed to deep link to benefiit
        if (!clickedOnBenefit && (!showModal || !selectedReward)) { // clicking on benefit should not trigger tab analytics
            window.digitalData.page.pageInfo.pageName = "nba:membership-hub:benefits";
            window.digitalData.page.pageInfo.siteSection = "nba:membership-hub";
            window.digitalData.page.content = { categoryTitle: "Benefits", categoryType: "membership-hub" };
            window.digitalData.site = { propertyName: "nba:web", league: "nba" };
            if (window.digitalData.user) {
                window.digitalData.user.userState = "authenticated";
                window.digitalData.user.userType = userType;
            }
            console.log("Benefits: analytics added");
        }

        injectPageScripts();
    }, [params]);

    useEffect(() => {
        if (!showModal) {
            setupBaseMetaData();
        }
        dispatch(getRewardSections("benefits"));
    }, [showModal, dispatch]);

    useEffect(() => {
        console.log("Benefits: useEffect[showModal]");

        const bodyElement = document.getElementById("body");
        if (bodyElement) {
            if (showModal) {
                if (!bodyElement.className.includes("modal-noscroll")) {
                    bodyElement.className += " modal-noscroll";
                }
            } else {
                if (bodyElement.className.includes("modal-noscroll")) {
                    bodyElement.className = bodyElement.className.replace(" modal-noscroll", "");
                }
            }
        }

    }, [showModal]);

    useEffect(() => {
        console.log("Benefits: useEffect (tabSelected, needs rewards)");
        dispatch(setTabSelected("benefits"));
        if (refreshBenefits) {
            dispatch(getRewardSections("benefits"));
            setRefreshBenefits(false);
        }
    }, [dispatch, rewardSections, refreshBenefits]);

    useEffect(() => {
        console.log("Benefits: useEffect[params, rewardSections]", rewardSections);

        const setupBenefitsMetaData = (reward) => {
            if (reward) {
                let title = `NBA ID | Benefits | ${reward.title}`;
                let desc = reward.description;
                let imageUrl = reward.image;

                setupMetaData(title, desc, imageUrl);
            }
        };

        if (params && params.rewardId && rewardSections) {
            for (const sectionKey in rewardSections) {
                if (Object.hasOwnProperty.call(rewardSections, sectionKey)) {
                    const section = rewardSections[sectionKey];
                    let reward = section.rewards.find(reward => String(reward.id) === String(params.rewardId));
                    if (reward) {
                        setupBenefitsMetaData(reward);
                        openModal(reward);
                        break;
                    }
                }
            }
        }
    }, [params, rewardSections]);

    useEffect(() => {
        const rewardSectionsToCount = [1, 5];
        if (rewardSections) {
            let count = new Set();
            for (const sectionKey of rewardSectionsToCount) {
                if (rewardSections[sectionKey]) {
                    const section = rewardSections[sectionKey];
                    if (section && section.rewards.length > 0) {
                        section.rewards.forEach(reward => {
                            count.add(reward.id);
                        });
                    }
                }
            }
            setRewardCount(count.size);
        }
    }, [rewardSections]);

    let renderModal = () => {
        return (
            <>
                {selectedReward &&
                    <div className="modal-container">
                        <div className="modal-background">
                        </div>
                        <div className="modal-dialog">
                            <RewardModal selectedReward={selectedReward} setShowModal={setShowModal} close={() => {
                                setSelectedReward(null);
                                setShowModal(false);
                            }} />
                        </div>
                    </div>
                }
            </>
        );
    };

    return (
        <div ref={refBenefits} id="benefits-container">
            <TabSectionHeader title={config.benefitsHeading} subtitle={config.benefitsEvergreenCopyDescription}
                count={rewardCount} countText="available" />

            <div className="title-bar"></div>

            {rewardSections["1"] && <RewardSection tabSelected="benefits" section={rewardSections["1"]} />}

            <div className="section responsive-width flex flex-col justify-start items-start flex-grow-0 flex-shrink-0">
                <div id="featuredbenefit1" className="section-id"></div>
            </div>

            <div className="section responsive-width flex flex-col justify-start items-start flex-grow-0 flex-shrink-0">
                <div id="localidbenefits" className="section-id"></div>
            </div>

            <div className="section-container">
                <div id="accessgranted" className="section-id"></div>
                <div className="section-header">
                    <div className="section-title"><p>{config.evergreenBenefitTitle}</p></div>
                    <div className="section-subtitle"><p>{config.evergreenBenefitDescription}</p></div>
                </div>
            </div>

            {rewardSections["5"] && <RewardSection tabSelected="benefits" section={rewardSections["5"]} />}

            <div className="padding-two-five flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-10 section-bottom-margin">
                <p className="section-header">
                    {config.benefitsQATitle}
                </p>
                <p className="section-subHeader">
                    Refer to our FAQ  <a style={{
                        textDecoration: "underline",
                        color: "rgb(2, 104, 214)"
                    }} href="https://support.watch.nba.com/hc/en-us/articles/8417903789591-NBA-ID-Benefits" target="_blank" rel="noreferrer">here</a>
                </p>
            </div>

            {showModal && renderModal()}
        </div>
    );
}

export default Benefits;
